import InfiniteScroll from 'react-infinite-scroll-component'
import React from 'react'

import { useToast } from '@wartek-id/toast'

import SearchResultCard from 'app/SearchPage/components/SearchResultCard'
import SearchResultCardPreloader from 'app/SearchPage/components/SearchResultCardPreloader'
import { useAPIDocumentCollectionCategoriesDocumentList } from 'api/document_collection/useAPIDocumentCollection'
import CategoryError from 'app/DocumentCollectionPage/components/CategoryError'
import DocumentEmptyState from 'app/DocumentCollectionPage/components/DocumentEmptyState'
import { useSETrackEvent } from 'app/DocumentCollectionPage/hooks/useSETrackEvent'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'

import type {
  ResponseSuccessGetDocumentCategoryDetail,
  ResponseSuccessGetDocumentCollection,
} from 'api/document_collection/useAPIDocumentCollection'

type DocumentListItemProps = {
  parentData?: ResponseSuccessGetDocumentCategoryDetail['data']
}

const PAGINATION_CONFIG = {
  limit: 10,
  offset: 0,
}
const DEFAULT_PAGINATION_META = {
  limit: PAGINATION_CONFIG.limit,
  offset: PAGINATION_CONFIG.offset,
  total: 0,
  isNextPageError: false,
}

const LoadingState = () => {
  return (
    <div className="px-4">
      {Array(10)
        .fill('')
        .map((_, index) => {
          return (
            <SearchResultCardPreloader
              key={`search-result-preloader-${index}`}
            />
          )
        })}
    </div>
  )
}

export const DocumentListItem = (props: DocumentListItemProps) => {
  const { parentData } = props
  const id = parentData?.id

  const {
    isLoading: isListLoading,
    data: documentList,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useAPIDocumentCollectionCategoriesDocumentList(id, PAGINATION_CONFIG)

  const toast = useToast()
  const { SETrackDocumentLandingPage } = useSETrackEvent()
  const [, , , getValue] = useLocalStorage('categoryLvl', null)

  const categoryLvl = getValue()

  const listResult = React.useMemo(() => {
    if (isListLoading) return []
    return documentList?.pages.flatMap((page) => page.data) ?? []
  }, [documentList?.pages, isListLoading])

  const listMeta: ResponseSuccessGetDocumentCollection['meta'] =
    React.useMemo(() => {
      if (isListLoading) return DEFAULT_PAGINATION_META
      return (
        documentList?.pages?.[(documentList?.pages ?? []).length - 1]?.meta ??
        DEFAULT_PAGINATION_META
      )
    }, [documentList?.pages, isListLoading])

  const clickLoadMoreHandler = React.useCallback((): void => {
    const currentPageIndex = (listMeta?.offset ?? 0) / (listMeta?.limit ?? 10)
    refetch({ refetchPage: (page, index) => index === currentPageIndex })
  }, [listMeta, refetch])

  React.useEffect(() => {
    if (listMeta?.isNextPageError) {
      toast({
        message: 'Terjadi kendala sistem, mohon coba lagi',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listMeta?.isNextPageError])

  if (isListLoading) {
    return <LoadingState />
  }

  if (!isListLoading && listResult.length === 0) {
    return <DocumentEmptyState />
  }

  return (
    <InfiniteScroll
      className="min-h-[calc(100vh-350px)] !overflow-hidden bg-white"
      dataLength={listResult.length || 0}
      next={fetchNextPage}
      hasMore={hasNextPage}
      loader={
        listMeta?.isNextPageError ? (
          <CategoryError onClick={clickLoadMoreHandler} />
        ) : (
          <LoadingState />
        )
      }
      endMessage={
        listMeta?.isNextPageError && (
          <CategoryError onClick={clickLoadMoreHandler} />
        )
      }
    >
      {listResult.map((document, index) => {
        return (
          <SearchResultCard
            key={`document-list-${document?.collectionId ?? index}`}
            item={document}
            isThumbnailRounded
            link={`/dokumen/${document?.collectionId}?parentCategory=${parentData?.parentName}`}
            onClick={() =>
              SETrackDocumentLandingPage('CLICK_KUMPULAN_DOKUMEN_MATERIAL', {
                screen_name: 'KUMPULAN_DOKUMEN_DOCUMENT_LIST_PAGE',
                event_trigger: 'KUMPULAN_DOKUMEN_DOCUMENT_LIST_PAGE',
                parent_category: parentData?.parentName,
                category: parentData?.name,
                category_level: categoryLvl,
                page_element: document?.title,
                url: `${window.location.origin}/dokumen/${document?.collectionId}`,
              })
            }
          />
        )
      })}
    </InfiniteScroll>
  )
}
